import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { LayoutDashboard, Briefcase, FileText, Users, Building2, Mailbox, MessageSquare,
  Star, HelpCircle, Layers, MapPin, Newspaper, Settings as SettingsIcon, LogOut, Menu, X, Bell, Workflow, CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/jobs", label: "Jobs", icon: Briefcase },
  { to: "/admin/applications", label: "Applications", icon: FileText },
  { to: "/admin/interviews", label: "Interviews", icon: CalendarClock },
  { to: "/admin/candidates", label: "Candidates", icon: Users },
  { to: "/admin/leads", label: "Leads / Requirements", icon: Mailbox },
  { to: "/admin/employers", label: "Employers", icon: Building2 },
  { to: "/admin/contacts", label: "Enquiries", icon: MessageSquare },
  { to: "/admin/blogs", label: "Blog", icon: Newspaper },
  { to: "/admin/services", label: "Services", icon: Layers },
  { to: "/admin/industries", label: "Industries", icon: Workflow },
  { to: "/admin/locations", label: "Locations", icon: MapPin },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
  { to: "/admin/testimonials", label: "Testimonials", icon: Star },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setUnread(r.data.unread_notifications || 0)).catch(() => {});
  }, [loc.pathname]);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#070A0F] text-slate-100 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 glass-strong border-r border-white/10 transform transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center px-5 border-b border-white/10">
          <Link to="/" className="font-display font-extrabold text-lg">Dreamzz<span className="text-brand-emerald">HR</span> <span className="text-xs text-slate-500 font-mono">/admin</span></Link>
        </div>
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-4rem)]">
          {NAV.map((n) => {
            const active = n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            return (
              <Link key={n.to} to={n.to} data-testid={`admin-nav-${n.label.toLowerCase().split(" ")[0]}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${active ? "bg-brand-emerald/15 text-brand-emerald" : "text-slate-300 hover:bg-white/5"}`}>
                <n.icon size={18} />{n.label}
              </Link>
            );
          })}
        </nav>
      </aside>
      {open && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 glass-strong border-b border-white/10 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30">
          <button className="lg:hidden p-2" onClick={() => setOpen((o) => !o)} data-testid="admin-menu-toggle">{open ? <X size={22} /> : <Menu size={22} />}</button>
          <div className="hidden lg:block font-display font-semibold">Admin Console</div>
          <div className="flex items-center gap-4">
            <div className="relative"><Bell size={20} className="text-slate-400" />{unread > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-brand-emerald text-[10px] grid place-items-center text-white">{unread}</span>}</div>
            <div className="text-sm text-slate-400 hidden sm:block">{user?.name}</div>
            <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-2 text-sm text-slate-300 hover:text-brand-emerald"><LogOut size={18} />Logout</button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 overflow-x-hidden"><Outlet /></main>
      </div>
    </div>
  );
}
